/**
 * knowledge-bases-page.tsx — Trang "Kho tri thức" (route /ai/knowledge): danh sách KHO độc lập.
 *
 * Mỗi kho có bộ Tài liệu / Hỏi đáp / Ảnh và video riêng (không gộp chung). Thẻ kho: tên, mô tả hoặc số nội dung,
 * "Số nhân viên AI đang dùng: n", ⚙ sửa tên / mô tả, 🗑 xoá (chặn khi nhân viên AI đang chọn kho; còn nội dung thì
 * hỏi chuyển sang kho khác hay xoá cả nội dung). Bấm thẻ → vào kho.
 * Link cũ /ai?tab=knowledge&category=<id> chuyển tới đây kèm ?legacy=<id> → mở đúng kho.
 * `?tao=1[&ten=<tên>]` (mở từ màn nhân viên AI / luồng tạo ở tab mới) → mở sẵn hộp "Tạo kho" rồi bỏ tham số khỏi URL.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ArrowLeft, BookOpen, Bot, Loader2, Plus, Search, Settings, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  useKnowledgeBases, useCreateKnowledgeBase, useUpdateKnowledgeBase, useDeleteKnowledgeBase, type KnowledgeBase,
} from '@/hooks/use-knowledge-bases'
import { baseCountsLabel, isBaseEmpty, knowledgeBaseUrl, legacyBaseTarget, type KnowledgePerms } from './kb-lib'

/**
 * Quyền Kho tri thức theo vai trò động (backend đòi knowledge.create / update / delete — không theo tên vai trò).
 * Quản lý / Nhân viên mặc định chỉ có `knowledge.view` → chỉ xem. `loaded` = đã tải xong danh sách quyền.
 */
export function useKnowledgePerms(): KnowledgePerms & { loaded: boolean } {
  const { data: perms } = useMyPermissions()
  return {
    canCreate: !!perms?.has('knowledge.create'),
    canUpdate: !!perms?.has('knowledge.update'),
    canDelete: !!perms?.has('knowledge.delete'),
    loaded: !!perms,
  }
}

export function KnowledgeBasesPage() {
  const { t } = useTranslation('aiKnow')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const perms = useKnowledgePerms()
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  const { data: bases, isLoading, isError } = useKnowledgeBases(q)
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [editing, setEditing] = useState<KnowledgeBase | null>(null)
  const [deleting, setDeleting] = useState<KnowledgeBase | null>(null)

  // Link cũ (?legacy=<id danh mục gốc cũ / id kho>) → mở thẳng kho tương ứng.
  const legacy = params.get('legacy')
  useEffect(() => {
    if (!legacy || !bases) return
    const target = legacyBaseTarget(legacy, bases)
    if (target) navigate(knowledgeBaseUrl(target), { replace: true })
  }, [legacy, bases, navigate])

  // ?tao=1 → mở sẵn hộp tạo kho (một lần — bỏ tham số để tải lại trang không mở lại). Chờ tải xong quyền.
  const wantCreate = params.get('tao') === '1'
  const { canCreate, loaded: permsLoaded } = perms
  useEffect(() => {
    if (!wantCreate || !permsLoaded) return
    if (canCreate) { setCreateName(params.get('ten') ?? ''); setCreateOpen(true) }
    else toast.info(t('bases.noCreatePermission'))
    setParams((prev) => { const next = new URLSearchParams(prev); next.delete('tao'); next.delete('ten'); return next }, { replace: true })
  }, [wantCreate, permsLoaded, canCreate, params, setParams, t])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" title={t('common:actions.back')} aria-label={t('common:actions.back')} onClick={() => navigate('/ai')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-medium">{t('bases.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('bases.subtitle')}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form
          className="flex min-w-0 flex-1 items-center gap-2 sm:max-w-md"
          onSubmit={(e) => { e.preventDefault(); setQ(draft) }}
        >
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" placeholder={t('bases.searchPlaceholder')} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={t('bases.searchPlaceholder')} />
          </div>
          <Button type="submit" variant="outline">{t('common:actions.search')}</Button>
        </form>
        <div className="flex-1" />
        {canCreate && <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> {t('bases.addButton')}</Button>}
      </div>

      {isLoading && !bases ? (
        <Loading label={t('bases.loading')} />
      ) : isError ? (
        <ErrorState message={t('bases.loadError')} />
      ) : (bases ?? []).length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><BookOpen className="h-6 w-6" /></div>
          <p className="mt-3 text-sm font-medium">{q ? t('bases.noMatch', { q }) : t('bases.none')}</p>
          {!q && canCreate && <p className="mt-1 text-xs text-muted-foreground">{t('bases.noneHint')}</p>}
          {!q && canCreate && <Button className="mt-4" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> {t('bases.createButton')}</Button>}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {(bases ?? []).map((b) => (
            <BaseCard
              key={b.id} base={b} perms={perms}
              onOpen={() => navigate(knowledgeBaseUrl(b.id))}
              onEdit={() => setEditing(b)}
              onDelete={() => setDeleting(b)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <BaseFormDialog
          initialName={createName}
          onClose={() => { setCreateOpen(false); setCreateName('') }}
          onSaved={(b) => navigate(knowledgeBaseUrl(b.id))}
        />
      )}
      {editing && <BaseFormDialog base={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteBaseDialog base={deleting} bases={bases ?? []} onClose={() => setDeleting(null)} />}
    </div>
  )
}

function BaseCard({ base, perms, onOpen, onEdit, onDelete }: {
  base: KnowledgeBase; perms: KnowledgePerms; onOpen: () => void; onEdit: () => void; onDelete: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const empty = isBaseEmpty(base)
  return (
    <div
      role="button" tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen() }}
      className="group flex cursor-pointer flex-col rounded-xl border bg-card p-4 shadow-sm transition-colors hover:border-primary/50 hover:shadow"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><BookOpen className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium" title={base.name}>{base.name}</p>
          {base.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{base.description}</p>}
        </div>
      </div>
      <p className={cn('mt-3 text-xs', empty ? 'text-muted-foreground' : 'text-foreground')}>{baseCountsLabel(base)}</p>
      <div className="mt-auto flex items-center gap-1 border-t pt-3 mt-3">
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground" title={botsTitle(base)}>
          <Bot className="h-3.5 w-3.5 shrink-0" /> <Trans t={t} i18nKey="detail.botCount" values={{ n: base.botCount }} components={{ b: <b className="text-foreground" /> }} />
        </span>
        {perms.canUpdate && (
          <Button
            variant="ghost" size="icon" className="h-7 w-7" title={t('detail.editTitle')} aria-label={t('bases.editAria', { name: base.name })}
            onClick={(e) => { e.stopPropagation(); onEdit() }}
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
        )}
        {perms.canDelete && (
          <Button
            variant="ghost" size="icon" className="h-7 w-7" title={t('bases.deleteTitle')} aria-label={t('bases.deleteAria', { name: base.name })}
            onClick={(e) => { e.stopPropagation(); onDelete() }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  )
}

function botsTitle(b: KnowledgeBase): string {
  const parts: string[] = []
  if (b.bots.length) parts.push(i18n.t('aiKnow:bases.botsPicked', { names: b.bots.map((x) => x.name).join(', ') }))
  if (b.allBasesBots.length) parts.push(i18n.t('aiKnow:bases.botsAll', { names: b.allBasesBots.map((x) => x.name).join(', ') }))
  return parts.join('\n') || i18n.t('aiKnow:bases.botsNone')
}

export function BaseFormDialog({ base, initialName, onClose, onSaved }: {
  base?: KnowledgeBase
  /** Tên điền sẵn khi tạo mới. */
  initialName?: string
  onClose: () => void
  onSaved?: (b: { id: string; name: string }) => void
}) {
  const { t } = useTranslation('aiKnow')
  const create = useCreateKnowledgeBase()
  const update = useUpdateKnowledgeBase()
  const [name, setName] = useState(base?.name ?? initialName ?? '')
  const [description, setDescription] = useState(base?.description ?? '')
  const busy = create.isPending || update.isPending

  async function save() {
    if (!name.trim()) { toast.error(t('bases.form.enterName')); return }
    try {
      const saved = base
        ? await update.mutateAsync({ id: base.id, name: name.trim(), description: description.trim() || null })
        : await create.mutateAsync({ name: name.trim(), description: description.trim() || null })
      toast.success(base ? t('bases.form.saved') : t('bases.form.created'))
      onSaved?.(saved)
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{base ? t('bases.form.titleEdit') : t('bases.form.titleNew')}</DialogTitle>
          <DialogDescription>{t('bases.form.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="kb-name">{t('bases.form.nameLabel')}</Label>
            <Input id="kb-name" autoFocus maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('bases.form.namePlaceholder')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kb-desc">{t('bases.form.descLabel')}</Label>
            <Textarea id="kb-desc" className="min-h-20 text-sm" maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('bases.form.descPlaceholder')} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button onClick={() => void save()} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}{base ? t('common:actions.save') : t('bases.form.create')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function DeleteBaseDialog({ base, bases, onClose }: { base: KnowledgeBase; bases: KnowledgeBase[]; onClose: () => void }) {
  const { t } = useTranslation('aiKnow')
  const del = useDeleteKnowledgeBase()
  const empty = isBaseEmpty(base)
  const others = bases.filter((b) => b.id !== base.id)
  const [mode, setMode] = useState<'move' | 'purge'>(others.length ? 'move' : 'purge')
  const [targetId, setTargetId] = useState(others[0]?.id ?? '')
  const blocked = base.bots.length > 0

  async function confirm() {
    try {
      if (empty) await del.mutateAsync({ id: base.id })
      else if (mode === 'move') {
        if (!targetId) { toast.error(t('bases.del.pickTarget')); return }
        await del.mutateAsync({ id: base.id, mode: 'move', targetId })
      } else await del.mutateAsync({ id: base.id, mode: 'purge' })
      toast.success(empty || mode === 'purge' ? t('bases.del.deleted') : t('bases.del.movedAndDeleted'))
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('bases.del.title', { name: base.name })}</DialogTitle>
          <DialogDescription>{empty ? t('bases.del.emptyNow') : baseCountsLabel(base)}</DialogDescription>
        </DialogHeader>
        {blocked ? (
          <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            <p className="font-medium">{t('bases.del.blocked')}</p>
            <p className="text-xs">{base.bots.map((b) => b.name).join(', ')}</p>
            <p className="text-xs text-muted-foreground">{t('bases.del.blockedHint')}</p>
          </div>
        ) : !empty && (
          <div className="space-y-2 text-sm">
            {others.length > 0 && (
              <label className={cn('flex cursor-pointer gap-2 rounded-lg border p-3', mode === 'move' && 'border-primary bg-primary/5')}>
                <input type="radio" name="kb-del" checked={mode === 'move'} onChange={() => setMode('move')} className="mt-1" />
                <span className="min-w-0 flex-1 space-y-2">
                  <span className="block font-medium">{t('bases.del.moveContent')}</span>
                  <Select value={targetId} onValueChange={setTargetId} disabled={mode !== 'move'}>
                    <SelectTrigger className="h-8"><SelectValue placeholder={t('bases.del.targetPlaceholder')} /></SelectTrigger>
                    <SelectContent>{others.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </span>
              </label>
            )}
            <label className={cn('flex cursor-pointer gap-2 rounded-lg border p-3', mode === 'purge' && 'border-destructive bg-destructive/5')}>
              <input type="radio" name="kb-del" checked={mode === 'purge'} onChange={() => setMode('purge')} className="mt-1" />
              <span>
                <span className="block font-medium">{t('bases.del.purge')}</span>
                <span className="block text-xs text-muted-foreground">{t('bases.del.purgeHint')}</span>
              </span>
            </label>
          </div>
        )}
        {base.allBasesBots.length > 0 && !blocked && (
          <p className="text-[11px] text-muted-foreground">{t('bases.del.allBasesNote', { n: base.allBasesBots.length })}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          {!blocked && (
            <Button variant="destructive" onClick={() => void confirm()} disabled={del.isPending}>
              {del.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {empty || mode === 'purge' ? t('bases.del.deleteBase') : t('bases.del.moveAndDelete')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
